import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import ProductCard from "../products/ProductCard";
import productsData from "../../assets/data/products";
import AnimatedSection from "../common/AnimatedSection";

const FeaturedProducts = () => {
  const [activeCategory, setActiveCategory] = useState("all");
  const [animateProducts, setAnimateProducts] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Check if productsData exists and is an array
  const featuredProducts = Array.isArray(productsData)
    ? productsData.filter((product) => product && product.isFeatured)
    : [];

  // If no featured products, don't render the section
  if (featuredProducts.length === 0) {
    return null;
  }

  // Create a balanced selection of products based on category
  const getFilteredProducts = () => {
    if (activeCategory === "all") {
      // For "all" category: show a balanced mix from different categories
      const mrlProduct = featuredProducts.find(
        (p) => p.category === "machine-room-less"
      );
      const hydraulicsProduct = featuredProducts.find(
        (p) => p.category === "hydraulics"
      );
      // const dumbwaiterProduct = featuredProducts.find(p => p.category === 'dumbwaiter');
      // Combine car-lift and freight-lift into vehicle-lift
      const vehicleLiftProduct = featuredProducts.find(
        (p) =>
          p.category === "vehicle-lift" ||
          p.category === "car-lift" ||
          p.category === "freight-lift"
      );

      // Combine the products, filter out any undefined values, and take the first 3
      return [mrlProduct, hydraulicsProduct, vehicleLiftProduct]
        .filter(Boolean)
        .slice(0, 3);
    } else if (activeCategory === "vehicle-lift") {
      // For vehicle lifts, combine both car and freight lifts
      return featuredProducts
        .filter(
          (product) =>
            product.category === "vehicle-lift" ||
            product.category === "car-lift" ||
            product.category === "freight-lift"
        )
        .slice(0, 3);
    } else {
      // For other specific categories: select up to 3 products from that category
      return featuredProducts
        .filter((product) => product.category === activeCategory)
        .slice(0, 3);
    }
  };

  // Get the filtered products based on active category
  const filteredProducts = getFilteredProducts();

  // Define the categories with icons (UPDATED - combined car/freight lift)
  const categories = [
    { id: "all", label: "All Products", icon: "boxes" },
    { id: "machine-room-less", label: "Machine Room Less", icon: "cogs" },
    { id: "hydraulics", label: "Hydraulics", icon: "tint" },
    // { id: 'dumbwaiter', label: 'Dumbwaiter', icon: 'utensils' },
    { id: "vehicle-lift", label: "Car/Freight Lift", icon: "truck" },
  ];

  // Handle category change with animation
  const handleCategoryChange = (categoryId) => {
    if (categoryId === activeCategory) return;

    setAnimateProducts(false);
    setTimeout(() => {
      setActiveCategory(categoryId);
      setAnimateProducts(true);
    }, 300);
  };

  // Initialize animation on first load
  useEffect(() => {
    setAnimateProducts(true);
  }, []);

  // Get responsive styles based on window width
  const getResponsiveStyles = () => {
    // Define breakpoints
    const isMobile = windowWidth <= 576;
    const isTablet = windowWidth <= 820 && windowWidth > 576;

    return {
      // Products grid responsive columns
      productsGrid: {
        display: "grid",
        gridTemplateColumns: isMobile
          ? "1fr"
          : isTablet
          ? "repeat(2, 1fr)"
          : "repeat(3, 1fr)",
        gap: isMobile ? "20px" : "30px",
        marginBottom: "50px",
        transition: "all 0.4s ease",
        opacity: animateProducts ? 1 : 0,
        transform: animateProducts ? "translateY(0)" : "translateY(20px)",
      },

      // Section title responsive font sizes
      sectionTitle: {
        fontSize: isMobile ? "2rem" : isTablet ? "2.4rem" : "2.8rem",
        fontWeight: "800",
        marginBottom: "20px",
        color: "#2c3e50",
      },

      // Category filters responsive styling
      categoryFilters: {
        display: "flex",
        justifyContent: isMobile ? "flex-start" : "center",
        flexWrap: isMobile ? "nowrap" : "wrap",
        gap: isMobile ? "10px" : "15px",
        maxWidth: "100%", // Increased to fit fewer categories better
        margin: "0 auto",
        backgroundColor: "rgba(255, 255, 255, 0.5)",
        padding: isMobile ? "12px 15px" : "15px 20px",
        borderRadius: "50px",
        boxShadow: "0 5px 20px rgba(0, 0, 0, 0.05)",
        overflowX: isMobile ? "auto" : "visible",
        WebkitOverflowScrolling: "touch",
      },

      // Category button responsive sizing
      categoryButton: {
        padding: isMobile ? "8px 12px" : isTablet ? "10px 16px" : "12px 20px",
        borderRadius: "30px",
        border: "1px solid #e2e8f0",
        fontSize: isMobile ? "0.75rem" : isTablet ? "0.85rem" : "0.95rem",
        fontWeight: "600",
        cursor: "pointer",
        transition: "all 0.3s ease",
        boxShadow: "0 3px 10px rgba(0, 0, 0, 0.05)",
        display: "flex",
        alignItems: "center",
        gap: "8px", // Increased gap for better readability
        whiteSpace: "nowrap",
        flexShrink: 0,
      },

      // Container padding adjustments for mobile
      container: {
        maxWidth: "1200px",
        margin: "0 auto",
        padding: isMobile ? "0 15px" : "0 20px",
        position: "relative",
        zIndex: 1,
      },

      // Section padding adjustments
      productSection: {
        padding: isMobile ? "40px 0" : isTablet ? "60px 0" : "80px 0",
        backgroundColor: "#f8fafc",
        position: "relative",
        overflow: "hidden",
      },

      // Section subtitle responsive sizing
      sectionSubtitle: {
        fontSize: isMobile ? "1rem" : "1.2rem",
        color: "#64748b",
        lineHeight: "1.6",
      },

      // Category filter container full width on mobile
      categoryContainer: {
        display: "flex",
        justifyContent: "center",
        marginBottom: "45px",
        width: "100%",
      },

      // View more button responsive sizing
      viewMoreButton: {
        backgroundColor: "#163ba2",
        color: "white",
        border: "none",
        borderRadius: "30px",
        padding: isMobile ? "14px 28px" : "16px 35px",
        fontSize: isMobile ? "1rem" : "1.1rem",
        fontWeight: "600",
        cursor: "pointer",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        transition: "all 0.3s ease",
        boxShadow: "0 8px 20px rgba(22, 59, 162, 0.2)",
      },
    };
  };

  // Get the responsive styles
  const responsiveStyles = getResponsiveStyles();

  return (
    <section
      style={{ ...styles.productSection, ...responsiveStyles.productSection }}
    >
      {/* Background accents */}
      <div style={styles.bgAccent1}></div>
      <div style={styles.bgAccent2}></div>

      <div style={{ ...styles.container, ...responsiveStyles.container }}>
        <div style={styles.titleContainer}>
          <h2
            style={{ ...styles.sectionTitle, ...responsiveStyles.sectionTitle }}
          >
            Our <span style={styles.highlight}>Offerings</span>
          </h2>
          <div style={styles.titleUnderline}>
            <div style={styles.titleUnderlinePrimary}></div>
          </div>
          <p
            style={{
              ...styles.sectionSubtitle,
              ...responsiveStyles.sectionSubtitle,
            }}
          >
            Discover our premium elevator solutions designed for various
            applications
          </p>
        </div>

        {/* Category tabs */}
        <div
          style={{
            ...styles.categoryContainer,
            ...responsiveStyles.categoryContainer,
          }}
        >
          <div
            style={{
              ...styles.categoryFilters,
              ...responsiveStyles.categoryFilters,
            }}
          >
            {categories.map((category) => (
              <button
                key={category.id}
                style={{
                  ...responsiveStyles.categoryButton,
                  backgroundColor:
                    activeCategory === category.id ? "#163ba2" : "white",
                  color: activeCategory === category.id ? "white" : "#333",
                }}
                onClick={() => handleCategoryChange(category.id)}
              >
                <i
                  className={`fas fa-${category.icon}`}
                  style={styles.categoryIcon}
                ></i>
                {category.label}
              </button>
            ))}
          </div>
        </div>

        {/* Products grid with animation */}
        <div style={responsiveStyles.productsGrid}>
          {filteredProducts.length > 0 ? (
            filteredProducts.map((product, index) => (
              <AnimatedSection
                key={product.id || index}
                animation="fadeInUp"
                delay={index * 0.1}
              >
                <div style={styles.productCardWrapper}>
                  <ProductCard
                    id={product.id}
                    title={product.name || "Product"}
                    content={product.description || "No description available"}
                    image={
                      product.image ||
                      "https://res.cloudinary.com/deecee-elevator/image/upload/v1/elevators/placeholder.jpg"
                    }
                    category={
                      product.category === "car-lift" ||
                      product.category === "freight-lift"
                        ? "Vehicle Lift"
                        : product.category
                    }
                  />
                </div>
              </AnimatedSection>
            ))
          ) : (
            <div style={styles.noProducts}>
              <i
                className="fas fa-exclamation-circle"
                style={styles.noProductsIcon}
              ></i>
              <p>No products available in this category</p>
              <button
                style={styles.resetButton}
                onClick={() => handleCategoryChange("all")}
              >
                View All Products
              </button>
            </div>
          )}
        </div>

        <div style={styles.viewMoreContainer}>
          <Link to="/products" style={styles.viewMoreLink}>
            <button
              style={{
                ...styles.viewMoreButton,
                ...responsiveStyles.viewMoreButton,
              }}
            >
              Customize Your Own
              <i className="fas fa-arrow-right" style={styles.buttonIcon}></i>
            </button>
          </Link>
        </div>
      </div>
    </section>
  );
};

const styles = {
  productSection: {
    // Base styles that aren't affected by responsiveness
    backgroundColor: "#f8fafc",
    position: "relative",
    overflow: "hidden",
  },
  bgAccent1: {
    position: "absolute",
    top: "-200px",
    right: "-100px",
    width: "600px",
    height: "600px",
    borderRadius: "50%",
    background:
      "radial-gradient(circle, rgba(22, 59, 162, 0.03) 0%, rgba(22, 59, 162, 0) 70%)",
  },
  bgAccent2: {
    position: "absolute",
    bottom: "-100px",
    left: "-200px",
    width: "600px",
    height: "600px",
    borderRadius: "50%",
    background:
      "radial-gradient(circle, rgba(22, 59, 162, 0.05) 0%, rgba(22, 59, 162, 0) 70%)",
  },
  titleContainer: {
    textAlign: "center",
    maxWidth: "700px",
    margin: "0 auto 50px",
  },
  highlight: {
    color: "#163ba2",
  },
  titleUnderline: {
    display: "flex",
    justifyContent: "center",
    marginBottom: "25px",
  },
  titleUnderlinePrimary: {
    width: "80px",
    height: "4px",
    backgroundColor: "#163ba2",
    borderRadius: "2px",
  },
  categoryIcon: {
    fontSize: "0.9rem",
  },
  productCardWrapper: {
    height: "100%",
  },
  noProducts: {
    gridColumn: "1 / -1",
    textAlign: "center",
    padding: "60px 20px",
    backgroundColor: "white",
    borderRadius: "15px",
    color: "#64748b",
    fontSize: "1.1rem",
    boxShadow: "0 5px 20px rgba(0, 0, 0, 0.05)",
  },
  noProductsIcon: {
    fontSize: "3rem",
    marginBottom: "20px",
    color: "#94a3b8",
  },
  resetButton: {
    padding: "12px 24px",
    backgroundColor: "#f1f5f9",
    color: "#333",
    border: "none",
    borderRadius: "30px",
    cursor: "pointer",
    fontWeight: "600",
    marginTop: "20px",
    transition: "all 0.3s ease",
  },
  viewMoreContainer: {
    textAlign: "center",
    marginTop: "30px",
  },
  viewMoreLink: {
    textDecoration: "none",
  },
  buttonIcon: {
    marginLeft: "10px",
  },
};

export default FeaturedProducts;
